import { CheckCircle2, CircleDashed, FlaskConical, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { JsonTree } from '@/components/JsonTree';
import { cn } from '@/lib/cn';
import type { BetBundle, BusinessClientSummary, RuleDefinition } from '@/types';
import { ClientSelect } from '@/features/bet-inspection/components/ClientSelect';
import { fetchBet } from '@/features/bet-inspection/lib/api';
import { evaluateRule, type PreviewResult } from '../lib/preview';

const SEV_STYLE: Record<string, string> = {
  critical: 'border-l-crit bg-crit-bg',
  warning: 'border-l-warn bg-warn-bg',
  info: 'border-l-info bg-info-bg',
};

/**
 * Live preview: load one real bet, then evaluate the DRAFT rule against it
 * instantly as the author types (same engine as the server). Shows whether it
 * fires, the `matched` value, and the rendered message — so nobody writes a rule
 * blind.
 */
export function RulePreview({
  draft,
  api,
  token,
  clients,
  clientsLoading,
  onResult,
  onBundle,
}: {
  draft: RuleDefinition;
  api: string;
  token: string | null;
  clients: BusinessClientSummary[];
  clientsLoading: boolean;
  onResult: (r: PreviewResult) => void;
  /** Lift the loaded sample bundle so the editor's autocomplete can navigate it. */
  onBundle?: (b: BetBundle | null) => void;
}) {
  const [psid, setPsid] = useState('');
  const [gameUuid, setGameUuid] = useState('');
  const [betId, setBetId] = useState('');
  const [bundle, setBundle] = useState<BetBundle | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [result, setResult] = useState<PreviewResult | null>(null);
  const [evaluating, setEvaluating] = useState(false);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const loadBet = async () => {
    if (!psid.trim() || !gameUuid.trim() || !betId.trim()) return;
    setLoading(true);
    setLoadError(null);
    try {
      const r = await fetchBet({
        api,
        token,
        input: { play_service_id: psid.trim(), game_uuid: gameUuid.trim(), bet_id: betId.trim() },
      });
      setBundle(r.bundle);
      onBundle?.(r.bundle);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
      setBundle(null);
      onBundle?.(null);
    } finally {
      setLoading(false);
    }
  };

  // Re-evaluate (debounced) whenever the draft or the sample bet changes.
  useEffect(() => {
    if (!bundle) return;
    let cancelled = false;
    setEvaluating(true);
    const t = setTimeout(async () => {
      const res = await evaluateRule(draft, bundle);
      if (cancelled) return;
      setResult(res);
      setEvaluating(false);
      onResultRef.current(res);
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [draft.expression, draft.message, JSON.stringify(draft.lists), bundle]);

  return (
    <div className="flex h-full flex-col border-l border-hairline">
      <div className="flex items-center gap-1.5 border-b border-hairline px-3 py-2">
        <FlaskConical size={12} className="text-accent" />
        <span className="eyebrow">live preview</span>
      </div>

      {/* sample bet loader */}
      <div className="space-y-2 border-b border-hairline p-3">
        <div className="eyebrow">sample bet</div>
        <ClientSelect value={psid} onChange={setPsid} clients={clients} loading={clientsLoading} />
        <input
          value={gameUuid}
          onChange={(e) => setGameUuid(e.target.value)}
          placeholder="game_uuid"
          spellCheck={false}
          className="w-full border border-hairline bg-surface-sunken px-2 py-1.5 font-mono text-[11.5px] text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
        />
        <div className="flex gap-2">
          <input
            value={betId}
            onChange={(e) => setBetId(e.target.value)}
            placeholder="bet_id"
            spellCheck={false}
            className="flex-1 border border-hairline bg-surface-sunken px-2 py-1.5 font-mono text-[11.5px] text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
          />
          <button
            type="button"
            onClick={loadBet}
            disabled={loading || !psid || !gameUuid || !betId}
            className="inline-flex items-center gap-1.5 border border-accent/40 bg-accent/10 px-3 font-condensed text-[10px] font-semibold uppercase tracking-wider text-accent hover:bg-accent/20 disabled:opacity-40"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : null}
            Load
          </button>
        </div>
        {loadError && <p className="font-mono text-[11px] text-crit">{loadError}</p>}
        {bundle && (
          <p className="text-[11px] text-text-dim">
            Loaded bet <span className="font-mono text-text">{bundle.resolved.bet_id}</span> ·{' '}
            {String(bundle.resolved.game?.name ?? '')} — edits preview live below.
          </p>
        )}
      </div>

      {/* result */}
      <div className="flex-1 overflow-auto p-3">
        {!bundle ? (
          <p className="text-[12px] text-text-dim">
            Load a bet to see whether this rule fires against it, and how its message renders.
          </p>
        ) : (
          <PreviewBody
            result={result}
            evaluating={evaluating}
            severity={draft.severity ?? 'info'}
            name={draft.name}
          />
        )}
      </div>
    </div>
  );
}

function PreviewBody({
  result,
  evaluating,
  severity,
  name,
}: {
  result: PreviewResult | null;
  evaluating: boolean;
  severity: string;
  name: string;
}) {
  if (!result) {
    return <p className="text-[12px] text-text-dim">{evaluating ? 'Evaluating…' : 'Waiting…'}</p>;
  }

  if (result.expressionError) {
    return <ErrorBox label="Expression error">{result.expressionError}</ErrorBox>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {result.fired ? (
          <>
            <CheckCircle2 size={15} className="text-ok" />
            <span className="font-condensed text-[12px] font-semibold uppercase tracking-wider text-ok">
              fires on this bet
            </span>
          </>
        ) : (
          <>
            <CircleDashed size={15} className="text-text-faint" />
            <span className="font-condensed text-[12px] font-semibold uppercase tracking-wider text-text-dim">
              does not fire
            </span>
          </>
        )}
        {evaluating && <Loader2 size={11} className="animate-spin text-text-faint" />}
      </div>

      {result.fired && (
        <>
          {/* the hint exactly as support would see it */}
          {result.templateError ? (
            <ErrorBox label="Message template error">{result.templateError}</ErrorBox>
          ) : (
            <div>
              <div className="eyebrow mb-1">rendered hint</div>
              <div className={cn('border-l-2 px-3 py-2', SEV_STYLE[severity])}>
                <div className="mb-0.5 font-condensed text-[10px] font-semibold uppercase tracking-wider text-text-dim">
                  {severity} · {name || '(unnamed)'}
                </div>
                <p className="text-[12.5px] leading-[1.5] text-text">{result.message}</p>
              </div>
            </div>
          )}

          <div>
            <div className="eyebrow mb-1">matched (available to the template)</div>
            <div className="border border-hairline bg-surface-sunken p-2">
              <JsonTree value={result.matched} defaultOpen={2} />
            </div>
          </div>
        </>
      )}

      {!result.fired && (
        <p className="text-[12px] text-text-dim">
          The expression returned nothing truthy for this bet — try a bet that should match, or
          adjust the expression.
        </p>
      )}
    </div>
  );
}

function ErrorBox({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-l-2 border-l-crit bg-crit-bg px-3 py-2">
      <div className="mb-0.5 font-condensed text-[10px] font-semibold uppercase tracking-wider text-crit">
        {label}
      </div>
      <p className="font-mono text-[11px] leading-snug text-text">{children}</p>
    </div>
  );
}
